import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import LogoMarquee from "../components/LogoMarquee";

const LOGOS = [
  { src: "/logos/tightwell.png", alt: "Tightwell Fasteners" },
  { src: "/logos/unitech.png", alt: "Unitech Gears" },
  { src: "/logos/greenfab.png", alt: "GreenFab Industries" },
  { src: "/logos/steelworks.png", alt: "SteelWorks India" },
  { src: "/logos/machinex.png", alt: "Machinex Pvt Ltd" },
];

const HERO_STATS = [
  { value: "100+", label: "Businesses served" },
  { value: "6+ yrs", label: "Avg. deployment lifespan" },
  { value: "Multi-branch", label: "Distributed rollouts" },
  { value: "ISO-grade", label: "Process compliance" },
];

const CLIENT_STORIES = [
  {
    name: "Dynamic Transmission Limited",
    industry: "Precision Manufacturing",
    solution: "Enterprise ERP",
    website: "http://www.dynamic-next.in/",
    description:
      "Part of the Tightwell Fasteners group, Dynamic Transmission manufactures automotive gears, high-tensile fasteners and precision turned components in carbon steel, alloy steel and stainless steel for the automobile, steel, cement, paper, chemical, power, aeronautics, railways and ship-building industries. Launched in 1990 with a workforce of 6, it has grown into an organisation of more than a thousand associates with a turnover of ₹80 Crore.",
    highlights: [
      "ERP running for 6+ years",
      "Multi-branch deployment",
      "₹80 Crore turnover",
      "1,000+ associates",
    ],
  },
  {
    name: "Newdynamic",
    industry: "Automotive Components",
    solution: "Enterprise ERP",
    website: "https://newdynamic.in/",
    description:
      "For more than 40 years, Newdynamic has been manufacturing high-quality automobile parts. Its wide and constantly growing product range includes transmission, steering, engine & drive and suspension components, aluminium PDC, hot & cold forging, oil pump assemblies and precision turned components. An ISO 9001:2000 certified group, it caters to the engineering, construction, automobile and other sectors.",
    highlights: [
      "Enterprise ERP for 5+ years",
      "ISO 9001:2000 certified",
      "12,000 M. Tons p.a. capacity",
      "40+ years of manufacturing",
    ],
  },
  {
    name: "King's College India",
    industry: "Education",
    solution: "School Management System",
    website: "https://www.kingscollegeindia.in/",
    description:
      "A partner school of the prestigious King's College Taunton in the UK, offering co-educational day, weekly and full boarding options for students from age 2.5. Established in 2015, it delivers a high-quality British education through the Cambridge Pathway, with a strong commitment to childcare and safeguarding.",
    highlights: [
      "School Management System",
      "Multi-branch deployment",
      "Established 2015",
      "Cambridge Pathway",
    ],
  },
];

const monogram = (name) =>
  name
    .replace(/[^a-zA-Z\s]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Clients = () => (
  <div className="clients-page">
    <section className="page-hero page-hero--enterprise">
      <div className="page-hero__glow" aria-hidden="true" />
      <motion.div
        className="page-hero__inner"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <p className="eyebrow eyebrow--invert">Trusted partnerships</p>
        <h1>Enterprises run their operations on Byteflow</h1>
        <p className="page-hero__lead">
          From precision manufacturing to multi-campus education, leading
          organisations rely on Byteflow to unify operations across every branch.
        </p>
      </motion.div>

      <motion.div
        className="hero-stats"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        {HERO_STATS.map((s) => (
          <div className="hero-stat" key={s.label}>
            <span className="hero-stat__value">{s.value}</span>
            <span className="hero-stat__label">{s.label}</span>
          </div>
        ))}
      </motion.div>
    </section>

    <section className="section section--muted">
      <p className="trust-band__label">Powering industry leaders</p>
      <LogoMarquee logos={LOGOS} speed={28} />
    </section>

    <section className="section">
      <motion.div
        className="section__header"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <p className="eyebrow">Client stories</p>
        <h2>Enterprises growing on Byteflow</h2>
        <p className="section__lead">
          Long-running, mission-critical deployments across manufacturing and
          education.
        </p>
      </motion.div>

      <div className="client-stories">
        {CLIENT_STORIES.map((c, i) => (
          <motion.article
            key={c.name}
            className="client-story"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
          >
            <div className="client-story__head">
              <span className="client-story__monogram" aria-hidden="true">
                {monogram(c.name)}
              </span>
              <div className="client-story__heading">
                <h3>{c.name}</h3>
                <span className="client-story__industry">{c.industry}</span>
              </div>
            </div>

            <span className="client-story__solution">{c.solution}</span>

            <p className="client-story__desc">{c.description}</p>

            <ul className="client-story__highlights">
              {c.highlights.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>

            <a
              className="client-story__link"
              href={c.website}
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit website
              <span aria-hidden="true">→</span>
            </a>
          </motion.article>
        ))}
      </div>
    </section>

    <section className="section section--muted">
      <motion.div
        className="section__header"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <p className="eyebrow">Featured customers</p>
        <h2>A few of the teams running on Byteflow</h2>
      </motion.div>

      <div className="clients-grid">
        {LOGOS.map((logo) => (
          <motion.div
            key={logo.src}
            className="client-tile"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4 }}
          >
            <img src={logo.src} alt={logo.alt} loading="lazy" />
          </motion.div>
        ))}
      </div>
    </section>

    <section className="cta-strip">
      <div className="cta-strip__inner">
        <div>
          <h2>Become a client</h2>
          <p>Join 100+ businesses already running on Byteflow.</p>
        </div>
        <Link to="/contact" className="btn-primary btn-primary--inverse">
          Request a Demo
        </Link>
      </div>
    </section>
  </div>
);

export default Clients;
